import { useTranslation } from 'react-i18next';
import type { SupplierRequestType } from '@econtractor/types';
import * as apiSupplierRequests from 'api/apiSupplierRequests';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import { useSendGenerationEmailDialog } from 'modules/templates';
import { buildSupplierRequestMailContent } from 'utils/helpers/email/buildMailContent';
import { SUPPLIER_REQUEST_STATUS } from './constants';

interface UseSupplierRequestSendEmailParams {
    onChanged: () => void;
}

const useSupplierRequestSendEmail = ({ onChanged }: UseSupplierRequestSendEmailParams) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const { openSendGenerationEmail } = useSendGenerationEmailDialog();

    const resolveVendorEmail = (row: SupplierRequestType): string => {
        const fromRow = String(row.sentEmail || '').trim();
        if (fromRow) return fromRow;
        const vendor = localSettings?.DIRS?.Vendor?.find(
            (item) => Number(item.ID) === Number(row.vendorId),
        );
        return String(vendor?.vendorEmail || '').trim();
    };

    const openSendEmail = (row: SupplierRequestType) => {
        if (Number(row.status) === SUPPLIER_REQUEST_STATUS.DELETED) return;

        openSendGenerationEmail({
            defaultEmail: resolveVendorEmail(row),
            labels: {
                title: t('supplierRequests.sendDialog.title'),
                emailLabel: t('supplierRequests.sendDialog.emailLabel'),
                emailPlaceholder: t('supplierRequests.sendDialog.emailPlaceholder'),
                emailHint: t('supplierRequests.sendDialog.emailHint'),
                emailRequired: t('supplierRequests.sendDialog.emailRequired'),
                emailInvalid: (emails) =>
                    t('supplierRequests.sendDialog.emailInvalid', { emails }),
                bodyLabel: t('supplierRequests.sendDialog.bodyLabel'),
                bodyPlaceholder: t('supplierRequests.sendDialog.bodyPlaceholder'),
                submit: t('supplierRequests.sendDialog.submit'),
                submitting: t('supplierRequests.sendDialog.submitting'),
            },
            onSubmit: async ({ email, emailBody }) => {
                const fileUrl = String(row.filePublicUrl || '').trim();
                if (!fileUrl) {
                    showToast(t('common:messError'), 'danger');
                    return false;
                }
                const mailContent = await buildSupplierRequestMailContent({
                    fileUrl,
                    emailBody,
                });
                if (!mailContent) {
                    showToast(t('common:messError'), 'danger');
                    return false;
                }
                const stat = await apiSupplierRequests.sendEmail(row.ID, {
                    email,
                    emailBody,
                    mailContent,
                });
                if (!stat) {
                    showToast(t('common:messError'), 'danger');
                    return false;
                }
                showToast(t('supplierRequests.sendDialog.success'), 'success');
                onChanged();
                return true;
            },
        });
    };

    return { openSendEmail };
};

export default useSupplierRequestSendEmail;
